import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { api } from '../../api/remembrApi';
import type {
  CreateShareRequest,
  ShareResponse,
  ShareListResponse,
  ShareManageRequest,
  VisibilityUpdateRequest,
  ShareViewResponse,
  ClaimRequest,
  SharingContact,
} from '../../api/sharingTypes';

// ==============================
// STATE INTERFACE
// ==============================

interface SharingState {
  // Data
  shares: ShareListResponse | null;
  contacts: SharingContact[];
  currentShare: ShareResponse | null;
  publicShare: ShareViewResponse | null;
  
  // Loading states
  loading: {
    shares: boolean;
    contacts: boolean;
    creating: boolean;
    claiming: boolean;
    managing: boolean;
    reminders: Record<number, boolean>;
    visibility: Record<number, boolean>; // Track loading per item ID
  };
  
  // Error handling
  error: string | null;
  
  // UI state
  shareModal: {
    isOpen: boolean;
    itemId: number | null;
    itemName: string | null;
    itemImageUrl: string | null;
    itemLocation: string | null;
  };
}

const initialState: SharingState = {
  shares: null,
  contacts: [],
  currentShare: null,
  publicShare: null,
  loading: {
    shares: false,
    contacts: false,
    creating: false,
    claiming: false,
    managing: false,
    reminders: {},
    visibility: {}, // Empty object to track loading per item ID
  },
  error: null,
  shareModal: {
    isOpen: false,
    itemId: null,
    itemName: null,
    itemImageUrl: null,
    itemLocation: null,
  },
};

// ==============================
// ASYNC THUNKS
// ==============================

// Create sharing link
export const createShare = createAsyncThunk(
  'sharing/createShare',
  async (
    { itemId, request }: { itemId: number; request: CreateShareRequest },
    { rejectWithValue },
  ) => {
    try {
      const response = await api.createShare(itemId, request);
      return response;
    } catch (err: any) {
      // Surface the FastAPI error detail so callers can display a
      // meaningful message instead of "Request failed with status
      // code 400". `.unwrap()` will reject with this string.
      const detail =
        err?.response?.data?.detail ||
        err?.message ||
        'Failed to create share link.';
      return rejectWithValue(
        typeof detail === 'string' ? detail : JSON.stringify(detail),
      );
    }
  },
);

// Load user's sharing activities
export const loadUserShares = createAsyncThunk(
  'sharing/loadUserShares',
  async (params?: {
    status?: string;
    sharing_type?: string;
    limit?: number;
    offset?: number;
  }) => {
    const response = await api.getUserShares(params);
    return response;
  }
);

// Manage share status
export const manageShare = createAsyncThunk(
  'sharing/manageShare',
  async ({ shareId, request }: { shareId: number; request: ShareManageRequest }, { rejectWithValue }) => {
    try {
      const response = await api.manageShare(shareId, request);
      return { shareId, response };
    } catch (error: any) {
      // Pass the full error with response data for better error handling
      return rejectWithValue({
        status: error.response?.status,
        data: error.response?.data,
        message: error.message
      });
    }
  }
);

// Send reminder to borrower (owner-only)
export const sendShareReminder = createAsyncThunk(
  'sharing/sendShareReminder',
  async (shareId: number, { rejectWithValue }) => {
    try {
      const response = await api.sendShareReminder(shareId);
      return { shareId, response };
    } catch (error: any) {
      return rejectWithValue({
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
        shareId,
      });
    }
  }
);

// Update item visibility
export const updateItemVisibility = createAsyncThunk(
  'sharing/updateItemVisibility',
  async ({ itemId, request }: { itemId: number; request: VisibilityUpdateRequest }) => {
    const response = await api.updateItemVisibility(itemId, request);
    return { itemId, response };
  }
);

// Load sharing contacts
export const loadSharingContacts = createAsyncThunk(
  'sharing/loadSharingContacts',
  async () => {
    const response = await api.getSharingContacts();
    return response.contacts;
  }
);

// View public share
export const viewPublicShare = createAsyncThunk(
  'sharing/viewPublicShare',
  async (shareToken: string) => {
    const response = await api.viewPublicShare(shareToken);
    return response;
  }
);

// Claim shared item
export const claimSharedItem = createAsyncThunk(
  'sharing/claimSharedItem',
  async ({ shareToken, request }: { shareToken: string; request: ClaimRequest }) => {
    const response = await api.claimSharedItem(shareToken, request);
    return response;
  }
);

// ==============================
// SLICE
// ==============================

const sharingSlice = createSlice({
  name: 'sharing',
  initialState,
  reducers: {
    // UI actions
    openShareModal: (
      state,
      action: PayloadAction<
        | number
        | {
            itemId: number;
            itemName?: string;
            itemImageUrl?: string;
            itemLocation?: string;
          }
      >,
    ) => {
      state.shareModal.isOpen = true;
      if (typeof action.payload === 'number') {
        state.shareModal.itemId = action.payload;
        state.shareModal.itemName = null;
        state.shareModal.itemImageUrl = null;
        state.shareModal.itemLocation = null;
      } else {
        state.shareModal.itemId = action.payload.itemId;
        state.shareModal.itemName = action.payload.itemName ?? null;
        state.shareModal.itemImageUrl = action.payload.itemImageUrl ?? null;
        state.shareModal.itemLocation = action.payload.itemLocation ?? null;
      }
    },
    closeShareModal: (state) => {
      state.shareModal.isOpen = false;
      state.shareModal.itemId = null;
      state.shareModal.itemName = null;
      state.shareModal.itemImageUrl = null;
      state.shareModal.itemLocation = null;
      state.currentShare = null;
    },
    clearError: (state) => {
      state.error = null;
    },
    resetSharingState: (state) => {
      state.shares = null;
      state.contacts = [];
      state.currentShare = null;
      state.publicShare = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Create Share
    builder
      .addCase(createShare.pending, (state) => {
        state.loading.creating = true;
        state.error = null;
      })
      .addCase(createShare.fulfilled, (state, action) => {
        state.loading.creating = false;
        state.currentShare = action.payload;
        state.error = null;
      })
      .addCase(createShare.rejected, (state, action) => {
        state.loading.creating = false;
        state.error = action.error.message || 'Failed to create share';
      });

    // Load User Shares
    builder
      .addCase(loadUserShares.pending, (state) => {
        state.loading.shares = true;
        state.error = null;
      })
      .addCase(loadUserShares.fulfilled, (state, action) => {
        state.loading.shares = false;
        state.shares = action.payload;
        state.error = null;
      })
      .addCase(loadUserShares.rejected, (state, action) => {
        state.loading.shares = false;
        state.error = action.error.message || 'Failed to load shares';
      });

    // Manage Share
    builder
      .addCase(manageShare.pending, (state) => {
        state.loading.managing = true;
        state.error = null;
      })
      .addCase(manageShare.fulfilled, (state, action) => {
        state.loading.managing = false;
        const { shareId, response } = action.payload;
        
        // Update the share status in the state
        if (state.shares) {
          const shareInSharedByMe = state.shares.shared_by_me.find(s => s.share_id === shareId);
          const shareInSharedWithMe = state.shares.shared_with_me.find(s => s.share_id === shareId);
          
          if (shareInSharedByMe) {
            shareInSharedByMe.status = response.new_status;
          }
          if (shareInSharedWithMe) {
            shareInSharedWithMe.status = response.new_status;
          }
        }
        state.error = null;
      })
      .addCase(manageShare.rejected, (state, action) => {
        state.loading.managing = false;
        // Don't set global error for validation errors (400 status)
        // These should be handled locally with alerts
        const errorPayload = action.payload as any;
        if (errorPayload?.status !== 400) {
          state.error = action.error.message || 'Failed to manage share';
        }
      });

    // Send Share Reminder
    builder
      .addCase(sendShareReminder.pending, (state, action) => {
        const shareId = action.meta.arg as number;
        state.loading.reminders[shareId] = true;
      })
      .addCase(sendShareReminder.fulfilled, (state, action) => {
        const shareId = action.payload.shareId as number;
        state.loading.reminders[shareId] = false;
        // Optionally update reminder metadata if present on the share entry
        // We keep it simple; backend owns can_send_reminder logic
      })
      .addCase(sendShareReminder.rejected, (state, action) => {
        const shareId = (action.payload as any)?.shareId ?? action.meta.arg;
        state.loading.reminders[shareId] = false;
      });

    // Update Item Visibility
    builder
      .addCase(updateItemVisibility.pending, (state, action) => {
        const itemId = action.meta.arg.itemId;
        state.loading.visibility[itemId] = true;
        state.error = null;
      })
      .addCase(updateItemVisibility.fulfilled, (state, action) => {
        const itemId = action.payload.itemId;
        state.loading.visibility[itemId] = false;
        state.error = null;
      })
      .addCase(updateItemVisibility.rejected, (state, action) => {
        const itemId = action.meta.arg.itemId;
        state.loading.visibility[itemId] = false;
        state.error = action.error.message || 'Failed to update visibility';
      });

    // Load Sharing Contacts
    builder
      .addCase(loadSharingContacts.pending, (state) => {
        state.loading.contacts = true;
        state.error = null;
      })
      .addCase(loadSharingContacts.fulfilled, (state, action) => {
        state.loading.contacts = false;
        state.contacts = action.payload;
        state.error = null;
      })
      .addCase(loadSharingContacts.rejected, (state, action) => {
        state.loading.contacts = false;
        state.error = action.error.message || 'Failed to load contacts';
      });

    // View Public Share
    builder
      .addCase(viewPublicShare.pending, (state) => {
        state.loading.shares = true;
        state.error = null;
      })
      .addCase(viewPublicShare.fulfilled, (state, action) => {
        state.loading.shares = false;
        state.publicShare = action.payload;
        state.error = null;
      })
      .addCase(viewPublicShare.rejected, (state, action) => {
        state.loading.shares = false;
        state.error = action.error.message || 'Failed to load public share';
      });

    // Claim Shared Item
    builder
      .addCase(claimSharedItem.pending, (state) => {
        state.loading.claiming = true;
        state.error = null;
      })
      .addCase(claimSharedItem.fulfilled, (state) => {
        state.loading.claiming = false;
        state.error = null;
      })
      .addCase(claimSharedItem.rejected, (state, action) => {
        state.loading.claiming = false;
        state.error = action.error.message || 'Failed to claim item';
      });
  },
});

// ==============================
// ACTIONS & SELECTORS
// ==============================

export const {
  openShareModal,
  closeShareModal,
  clearError,
  resetSharingState,
} = sharingSlice.actions;

// Selectors
// Defensive: tolerate stores that don't have the sharing slice
// registered (CustodySection/CustodyChip unit tests, etc.). Returns
// null when slice missing — callers already null-check shares.
export const selectShares = (state: { sharing?: SharingState }) =>
  state.sharing?.shares ?? null;
export const selectContacts = (state: { sharing: SharingState }) => state.sharing.contacts;
export const selectCurrentShare = (state: { sharing: SharingState }) => state.sharing.currentShare;
export const selectPublicShare = (state: { sharing: SharingState }) => state.sharing.publicShare;
export const selectSharingLoading = (state: { sharing: SharingState }) => state.sharing.loading;
export const selectSharingError = (state: { sharing: SharingState }) => state.sharing.error;
export const selectShareModal = (state: { sharing: SharingState }) => state.sharing.shareModal;
export const selectItemVisibilityLoading = (itemId: number) => (state: { sharing: SharingState }) =>
  state.sharing.loading.visibility[itemId] || false;

/* ──────────────────────────────────────────────────────────────────────
 * Sprint 8 Phase C — aggregate share selectors
 *
 * The `shares` slice already pulls `{ shared_by_me, shared_with_me }`
 * from /users/shares via `loadUserShares`. These selectors slice that
 * cache into the views the Share tab surfaces (and the dedicated
 * MySharesScreen reuses). All selectors silently return empty arrays
 * when the cache is null (pre-fetch / 404 / not signed in) so
 * consumers can render their empty-state without extra null checks.
 * ────────────────────────────────────────────────────────────────────── */

/** Inbound shares the recipient hasn't claimed yet — drives the
 *  "Available to claim" section. Filtered to PENDING status only
 *  (CLAIMED / RETURNED / DECLINED / CANCELLED move out of the
 *  call-to-action surface). */
export const selectInboundPendingShares = (state: {
  sharing?: SharingState;
}) =>
  (state.sharing?.shares?.shared_with_me ?? []).filter(
    (s) => s.status === 'PENDING',
  );

/** Outbound shares the owner created that are still LIVE — i.e.
 *  PENDING (awaiting claim) or CLAIMED (currently held by the
 *  recipient). Excludes RETURNED / DECLINED / CANCELLED /
 *  COMPLETED — those are done events, not active state. Drives
 *  "Shares I sent" section. */
export const selectOutboundActiveShares = (state: {
  sharing?: SharingState;
}) =>
  (state.sharing?.shares?.shared_by_me ?? []).filter(
    (s) => s.status === 'PENDING' || s.status === 'CLAIMED',
  );

/** Server-truth count of inbound pending shares — drives the
 *  Share-tab section badge "(N)". */
export const selectInboundPendingCount = (state: {
  sharing?: SharingState;
}): number => selectInboundPendingShares(state).length;

/** Server-truth count of outbound live shares (PENDING + CLAIMED) —
 *  drives the Share-tab section badge "(N)". */
export const selectOutboundActiveCount = (state: {
  sharing?: SharingState;
}): number => selectOutboundActiveShares(state).length;

/** Inbound shares I have CLAIMED — i.e. items currently with me via
 *  the share flow. Drives "Currently with me" section's share-backed
 *  rows. (Custody-handoffs inside crews are NOT in this list; those
 *  surface via the crew's own item view today.) */
export const selectInboundClaimedShares = (state: {
  sharing?: SharingState;
}) =>
  (state.sharing?.shares?.shared_with_me ?? []).filter(
    (s) => s.status === 'CLAIMED',
  );

export const selectInboundClaimedCount = (state: {
  sharing?: SharingState;
}): number => selectInboundClaimedShares(state).length;

/** PR10 — Inbound shares for the borrower across the FULL return
 *  lifecycle (CLAIMED → RETURNED → COMPLETED). Used by ItemDetail to
 *  keep the share-claim context resolvable AFTER the borrower has
 *  tapped Mark returned, so we can show "Returned · awaiting
 *  confirmation" instead of losing the share row entirely.
 *
 *  Excludes terminal/aborted states (DECLINED, CANCELLED) — those
 *  rows shouldn't render any banner.
 */
export const selectInboundLifecycleShares = (state: {
  sharing?: SharingState;
}) =>
  (state.sharing?.shares?.shared_with_me ?? []).filter(
    (s) =>
      s.status === 'CLAIMED' ||
      s.status === 'RETURNED' ||
      s.status === 'COMPLETED',
  );

/** PR10 — Lender-side companion: outbound shares across the full
 *  return lifecycle. Drives the lender's "Confirm receipt" surfaces
 *  (PR12) by exposing shares in RETURNED status, plus the post-
 *  confirmation COMPLETED rows so synthetic custody events keep
 *  rendering. */
export const selectOutboundLifecycleShares = (state: {
  sharing?: SharingState;
}) =>
  (state.sharing?.shares?.shared_by_me ?? []).filter(
    (s) =>
      s.status === 'CLAIMED' ||
      s.status === 'RETURNED' ||
      s.status === 'COMPLETED',
  );

export default sharingSlice.reducer;